"use client";

import { useEffect, useState } from "react";

import { usePageData } from "context/PageDataContext";
import {
  getLink,
  getCmsLocale,
  getTranslatedHref,
  isOverviewSingletonRecord,
  useBreakpoint,
} from "utils";
import { useLocale } from "next-intl";
import { Breakpoints, ILink, IconType } from "@types";
import { Icon } from "components/atoms/Icon/Icon";
import { BE_LOCALES, getPublicLocale, LU_LOCALES, Locale } from "consts/locale";
import s from "./LanguageToggle.module.scss";

export const LanguageToggle = () => {
  const { fields, titles, slugs } = usePageData();
  const locale = useLocale() as Locale;
  const relatedLocales = locale.endsWith("-LU") ? LU_LOCALES : BE_LOCALES;

  const breakpoint = useBreakpoint();

  const [openLanguageDropdown, setOpenLanguageDropdown] = useState(false);

  const [dropdownHover, setDropdownHover] = useState<string | null>();

  useEffect(() => {
    let timeOutId: NodeJS.Timeout | undefined;

    if (breakpoint !== Breakpoints.mobile) {
      if (dropdownHover) {
        clearTimeout(timeOutId);
        setOpenLanguageDropdown(true);
      } else {
        timeOutId = setTimeout(() => {
          setOpenLanguageDropdown(false);
        }, 500);
      }
    }

    return () => clearTimeout(timeOutId);
  }, [dropdownHover, breakpoint]);

  const getTranslatedLink = (lang: Locale) => {
    if (!fields) return { href: "/" };

    // Check if the title (and thus page) exist in the other locales
    // If not, return the base link
    const title = titles.page.find((pageTitle) => pageTitle.locale === getCmsLocale(lang));

    if (fields.__typename === "OfficeRecord") {
      if (!title) return { href: "/" };
      return getLink(fields, lang);
    }

    const baseLink = getLink(fields, lang);
    const { page: pageSlugs, parent: parentSlugs } = slugs;
    const localSlug = pageSlugs?.find((pageSlugItem) => pageSlugItem.locale === getCmsLocale(lang));
    const localParentSlug = parentSlugs?.find(
      (parentSlugItem) => parentSlugItem.locale === getCmsLocale(lang),
    );
    const isJobPage = fields.__typename === "JobListingLayoutRecord";
    const currentLanguage = getPublicLocale(locale);
    if (currentLanguage === "fr" && isJobPage && !fields.hasDutchTranslation)
      return { href: "/career/jobs#jobs" };
    if (currentLanguage === "nl" && isJobPage && !fields.hasFrenchTranslation)
      return { href: "/career/jobs#jobs" };

    // Singleton overviews: links are valid for every locale on the site; `titles.page` may omit
    // locales when content is filled via GraphQL fallback (common on LU for accountancy, etc.).
    if (isOverviewSingletonRecord(fields.__typename)) {
      return baseLink;
    }

    if (!title) return { href: "/" };

    return {
      ...baseLink,
      slug: localParentSlug
        ? `/${localParentSlug.value}/${localSlug?.value ?? ""}`
        : `/${localSlug?.value ?? ""}`,
    };
  };

  return (
    <div className={s.Toggle} data-open={openLanguageDropdown}>
      <button
        className={s.ToggleButton}
        type="button"
        onClick={() =>
          breakpoint === Breakpoints.mobile &&
          setOpenLanguageDropdown(openLanguageDropdown !== true)
        }
        onMouseOver={() => setDropdownHover("language-dropdown")}
        onMouseLeave={() => setDropdownHover(null)}
        onFocus={() => setDropdownHover("language-dropdown")}
      >
        {getPublicLocale(locale)?.toUpperCase()}
        <Icon name={IconType.CaretFilledDown} size="s" />
      </button>
      <ul
        className={s.Dropdown}
        onMouseOver={() => setDropdownHover("language-dropdown")}
        onMouseLeave={() => setDropdownHover(null)}
        onFocus={() => setDropdownHover("language-dropdown")}
      >
        {relatedLocales.map(
          (lang) =>
            lang !== locale && (
              <li key={lang}>
                <LanguageToggleLink key={lang} locale={lang} link={getTranslatedLink(lang)} />
              </li>
            ),
        )}
      </ul>
    </div>
  );
};

export const LanguageToggleLink = ({ locale, link }: { locale: Locale; link: ILink }) => {
  const currentLocale = useLocale() as Locale;
  if (locale === currentLocale) {
    return <span className={s.LinkActive}>{getPublicLocale(locale).toUpperCase()}</span>;
  }
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Force full page reload
    e.preventDefault();
    window.location.href = getTranslatedHref(link, locale);
  };
  return (
    <a
      href={getTranslatedHref(link, locale)}
      className={s.Link}
      hrefLang={locale}
      onClick={handleClick}
    >
      {getPublicLocale(locale).toUpperCase()}
    </a>
  );
};
